/**
 * CANONICAL MODULE — app-platform/shared/appUpdate.ts
 *
 * Copied byte-for-byte into every app (`src/lib/appUpdate.ts`). It imports nothing from any app and
 * nothing from vite-plugin-pwa: every app registers its worker through this module, whether the
 * worker is generated (contently, inkling, quil, trakr) or hand-rolled (bindr). Design:
 * design/W14-pwa.md. **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * It replaces five things that were written four or five times: the registration (above or inside
 * the auth gate), the update-check loop (hourly, visibility, both, neither), the "a new version is
 * ready" offer, the apply-and-reload step, and contently's boot-time stale-build gate (F14), which
 * no other app had. The update toast stays per app (each app's design contract owns its look); its
 * WORDS and its one action come from `updateNotice` here.
 *
 * ── Four rules, each with the history that made it ──────────────────────────────────────────────
 *
 * 1. REGISTER ABOVE EVERY GATE. bindr's reasoning (AppGate.tsx): "an app sitting on the sign-in
 *    screen ... is exactly the copy most likely to have been left open long enough to go stale."
 *    quil registered inside AuthGate, so a signed-out visitor never got a worker at all: no offline
 *    shell, no install, and the 2026-09-17 offline check could not even test it (B4).
 *
 * 2. NEVER skipWaiting ON INSTALL. A new worker WAITS until the user says Reload, then activates
 *    and the page reloads once. inkling learned this as a white screen (its CLAUDE.md): skipWaiting
 *    swapped assets mid-session, a stale cached index.html pointed at chunk hashes that no longer
 *    existed, and the SPA fallback served HTML where a JS module was expected. Every worker in the
 *    estate (workbox `registerType: 'prompt'`, bindr's sw.js) listens for
 *    `{ type: 'SKIP_WAITING' }` and does nothing until it arrives. Do not "simplify" this back.
 *
 * 3. A STALE BUILD MUST NOT TOUCH NEWER DATA. contently's BuildGate (F14): at boot, before anything
 *    reads or writes OneDrive, fetch /version.json (no-store) and if the deployed build is not this
 *    one, take the new worker over and reload. It FAILS OPEN at every step (offline, no worker
 *    support, a timeout, a malformed file): a broken check must never strand the boot. And if the
 *    data says it was written by a newer format (W12's `NewerDataError`) while no newer build can
 *    be reached, the app opens READ-ONLY rather than run old code over new data.
 *
 * 4. NOTHING AT THE INSTANT OF INSTALL. A worker reaching `installed` is offered only after it has
 *    stayed waiting for `SETTLE_MS`; one that activates itself (the kill switch) is never offered.
 *    A page fetch at that instant stalled the kill switch in Edge, in four apps (`watchForWaiting`).
 *
 * ── Kill switch ──────────────────────────────────────────────────────────────────────────────────
 * The worker-side kill switch is `scripts/sw-kill-switch.js` (W14 §3.6), which reaches users even
 * when this code never runs. (A page-side `resetServiceWorker` existed until 2026-11-01; no app
 * ever called it, so it was deleted under the every-loaded-byte-does-something rule, task 8.3.)
 */

// ─────────────────────────────────────────────────────────────────────────────── build identity

/** What `scripts/build-version.mjs` bakes in as `__BUILD_INFO__` and publishes as version.json. */
export interface BuildInfo {
  buildId: string
  sha?: string
  /** ISO time of the build. */
  time?: string
  /** The one-line "what changed" note (whatsNew.ts), possibly empty. */
  note?: string
}

export const VERSION_PATH = '/version.json'

/** A version.json body, or null if it is not one. Accepts quil's old `{buildTime, buildSha}`. */
export function parseVersionJson(body: unknown): BuildInfo | null {
  if (body === null || typeof body !== 'object') return null
  const b = body as Record<string, unknown>
  const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined)
  const buildId = str(b.buildId) ?? (str(b.buildSha) && str(b.buildTime) ? `${String(b.buildSha)}-${String(b.buildTime)}` : undefined)
  if (!buildId) return null
  const info: BuildInfo = { buildId }
  const sha = str(b.sha) ?? str(b.buildSha)
  const time = str(b.time) ?? str(b.buildTime)
  const note = str(b.note)
  if (sha) info.sha = sha
  if (time) info.time = time
  if (note) info.note = note
  return info
}

export type DeployedCheck =
  | { state: 'current' }
  | { state: 'behind'; deployed: BuildInfo }
  | { state: 'unknown'; reason: string }

export interface CheckOptions {
  fetchImpl?: typeof fetch
  url?: string
  timeoutMs?: number
  now?: () => number
}

/**
 * Is the deployed build this one? Never throws, never blocks longer than `timeoutMs`: every failure
 * is `unknown`, which every caller treats as "carry on" (fail open). `no-store` plus a query
 * parameter, so neither the HTTP cache nor a worker that (wrongly) cached the file can answer.
 */
export async function checkDeployedBuild(runningId: string, opts: CheckOptions = {}): Promise<DeployedCheck> {
  const fetchImpl = opts.fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined)
  if (!fetchImpl) return { state: 'unknown', reason: 'no fetch' }
  const now = opts.now ?? Date.now
  const url = `${opts.url ?? VERSION_PATH}?t=${now()}`
  const timeoutMs = opts.timeoutMs ?? 5000
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const timeout = new Promise<'timeout'>((res) => {
      timer = setTimeout(() => res('timeout'), timeoutMs)
    })
    const res = await Promise.race([fetchImpl(url, { cache: 'no-store' }), timeout])
    if (res === 'timeout') return { state: 'unknown', reason: 'timeout' }
    if (!res.ok) return { state: 'unknown', reason: `status ${res.status}` }
    const deployed = parseVersionJson(await res.json())
    if (!deployed) return { state: 'unknown', reason: 'malformed version.json' }
    return deployed.buildId === runningId ? { state: 'current' } : { state: 'behind', deployed }
  } catch (error) {
    return { state: 'unknown', reason: error instanceof Error ? error.message : 'fetch failed' }
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

// ──────────────────────────────────────────────────────────────────────── the stale-build gate

export type BootAction =
  /** Carry on. `stale` true means a newer build exists but could not be taken: offer the toast. */
  | { action: 'run'; stale: boolean }
  /** Hold the app (no OneDrive) and take the new build: `takeOverAndReload`. */
  | { action: 'update'; deployed: BuildInfo }
  /** Open, but write nothing: the data is from a newer build and this one cannot update now. */
  | { action: 'read-only'; reason: string }

export interface BootInput {
  check: DeployedCheck
  /** `'serviceWorker' in navigator`. */
  swSupported: boolean
  /** A W12 `NewerDataError` was raised opening local or remote data. */
  newerData?: boolean
  /** This deployed buildId has already been tried this session (see `reloadGuard`). */
  alreadyTried?: boolean
}

/**
 * The gate's decision, pure. The table (design/W14-pwa.md §3.1):
 *
 *   check     newerData  tried   ->  action
 *   behind    any        no      ->  update        (with or without a worker: a reload fetches it)
 *   behind    no         yes     ->  run, stale    (fail open; the toast still offers it)
 *   behind    yes        yes     ->  read-only     (old code, new data, and the update did not take)
 *   current   no         -       ->  run
 *   unknown   no         -       ->  run           (offline or a broken check: fail open)
 *   current/unknown  yes  -      ->  read-only     (no newer build reachable to read it with)
 */
export function decideBoot(input: BootInput): BootAction {
  const { check, newerData = false, alreadyTried = false } = input
  if (check.state === 'behind') {
    if (!alreadyTried) return { action: 'update', deployed: check.deployed }
    return newerData ? { action: 'read-only', reason: 'newer data, and the update to the newer build did not take' } : { action: 'run', stale: true }
  }
  if (newerData) return { action: 'read-only', reason: check.state === 'current' ? 'data from a build newer than the deployed one' : 'newer data and no way to reach a newer build' }
  return { action: 'run', stale: false }
}

/** Minimal Storage, so tests and SSR need no DOM. */
export interface KeyValue {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/**
 * One automatic reload per deployed build per session. Without it, a CDN that serves the new
 * version.json before the new worker script (both are propagating) reloads forever. Returns true
 * the FIRST time it is asked about a buildId (go ahead), false after.
 */
export function reloadGuard(storage: KeyValue | null | undefined, deployedId: string, key = 'app-update:tried'): boolean {
  try {
    if (!storage) return true
    if (storage.getItem(key) === deployedId) return false
    storage.setItem(key, deployedId)
    return true
  } catch {
    return true
  }
}

/** Has this session already tried to take this build? (Read-only twin of `reloadGuard`.) */
export function alreadyTried(storage: KeyValue | null | undefined, deployedId: string, key = 'app-update:tried'): boolean {
  try {
    return storage?.getItem(key) === deployedId
  } catch {
    return false
  }
}

// ─────────────────────────────────────────────────────────────────── worker plumbing (DOM-shaped)

/** The parts of `ServiceWorkerContainer` this module uses. */
export interface SwContainer {
  controller: unknown
  register(url: string, opts?: { scope?: string }): Promise<SwRegistration>
  getRegistration(): Promise<SwRegistration | undefined>
  addEventListener(type: 'controllerchange', fn: () => void): void
  removeEventListener(type: 'controllerchange', fn: () => void): void
}
export interface SwWorker {
  state: string
  postMessage(msg: unknown): void
  addEventListener(type: 'statechange', fn: () => void): void
}
export interface SwRegistration {
  waiting: SwWorker | null
  installing: SwWorker | null
  update(): Promise<unknown>
  addEventListener(type: 'updatefound', fn: () => void): void
}

export const SKIP_WAITING = { type: 'SKIP_WAITING' } as const

const container = (): SwContainer | null =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? (navigator.serviceWorker as unknown as SwContainer) : null

/**
 * Register the worker. Call it ABOVE every gate (rule 1), once, in production builds only. Never
 * throws: no support or a failed registration is null, and the app runs without a worker.
 */
export async function registerServiceWorker(opts: { url?: string; scope?: string; container?: SwContainer | null } = {}): Promise<SwRegistration | null> {
  const c = opts.container === undefined ? container() : opts.container
  if (!c) return null
  try {
    return await c.register(opts.url ?? '/sw.js', { scope: opts.scope ?? '/' })
  } catch {
    return null
  }
}

/**
 * How long a newly installed worker must stay WAITING before it is offered (rule 4 below).
 * Measured 2026-10-08: the stall happens within the first moments of install; contently and quil
 * settled on 3 s, inkling on 2 s, and every one of them then passed offline-check --kill-switch.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export const SETTLE_MS = 3000

export interface WatchOptions {
  /** Override the settle period (default `SETTLE_MS`). 0 still defers to the next tick. */
  settleMs?: number
}

/**
 * Call `onReady` once when a NEW worker is installed and waiting (rule 2). Silent on a first
 * install (no controller yet: there is nothing to update from). bindr's `watchRegistration`.
 *
 * Rule 4, NOTHING AT THE INSTANT OF INSTALL (the kill-switch hang, hit independently in bindr,
 * inkling, quil and contently during the W14 adoption, 2026-10-08). The offer used to fire the moment
 * a worker reached `installed`, and every app then fetched version.json (or a lazy toast chunk) to
 * describe it. In Edge, a page fetch through the old worker at that instant, when the new worker was
 * the kill switch (which skips waiting by itself), stalled the switch's activation for good: it sat
 * `installed`, every later navigation hung behind it, and the rescue never ran. So an update reaching
 * `installed` is offered only once it has STAYED waiting for `settleMs`, and a worker that activates
 * itself (the kill switch; or another tab pressed Reload) or goes redundant in that time is never
 * offered at all. Nothing the caller does on the offer can then land on that instant.
 *
 * Considered and not chosen as the canonical fix: bindr's "ask the waiting worker for its build over
 * a MessageChannel". It also avoids the fetch, but only a worker that answers the message (bindr's
 * hand-rolled one) can use it; the workbox workers cannot, and it leaves the offer, and whatever else
 * an app does on it, at the dangerous instant. It stays a fine way for bindr to DESCRIBE the update,
 * on top of this.
 *
 * A worker already waiting when the page registers has been waiting since before this page existed,
 * so it is offered at once, unless it is already activating.
 */
export function watchForWaiting(reg: SwRegistration, c: Pick<SwContainer, 'controller'>, onReady: (waiting: SwWorker) => void, opts: WatchOptions = {}): () => void {
  const settleMs = opts.settleMs ?? SETTLE_MS
  let offered = false
  let live = true
  const timers = new Set<ReturnType<typeof setTimeout>>()
  const stillWaiting = (w: SwWorker) => w.state === 'installed' && (!reg.waiting || reg.waiting === w)
  const offer = (w: SwWorker | null) => {
    if (!live || offered || !w || !c.controller || w.state !== 'installed') return
    offered = true
    onReady(w)
  }
  offer(reg.waiting)
  reg.addEventListener('updatefound', () => {
    const w = reg.installing
    if (!w) return
    let settling = false
    w.addEventListener('statechange', () => {
      if (w.state !== 'installed' || settling) return
      settling = true
      const timer = setTimeout(() => {
        timers.delete(timer)
        if (stillWaiting(w)) offer(w)
      }, settleMs)
      timers.add(timer)
    })
  })
  return () => {
    live = false
    for (const t of timers) clearTimeout(t)
    timers.clear()
  }
}

export interface UpdateLoopOptions {
  /** Unconditional re-check (contently, inkling, quil: hourly). */
  intervalMs?: number
  /** A visibility re-check happens only if this long has passed since the last (bindr: 15 min). */
  minGapMs?: number
  doc?: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'> | null
  now?: () => number
  setIntervalFn?: (fn: () => void, ms: number) => unknown
  clearIntervalFn?: (id: unknown) => void
}

/**
 * Re-check for a new worker hourly AND whenever the tab comes back into view (quil's idea, bindr's
 * throttle). Chrome throttles background `registration.update()` on Android (contently's
 * #android-pwa-misses-updates), so the visibility check is the one that actually fires for an
 * installed app. Rejections (offline) are swallowed. Returns a disposer.
 */
export function startUpdateLoop(reg: Pick<SwRegistration, 'update'>, opts: UpdateLoopOptions = {}): () => void {
  const now = opts.now ?? Date.now
  const minGap = opts.minGapMs ?? 15 * 60 * 1000
  const doc = opts.doc === undefined ? (typeof document !== 'undefined' ? document : null) : opts.doc
  const setI = opts.setIntervalFn ?? ((fn: () => void, ms: number) => setInterval(fn, ms))
  const clearI = opts.clearIntervalFn ?? ((id: unknown) => clearInterval(id as ReturnType<typeof setInterval>))
  let last = now()
  const check = () => {
    last = now()
    reg.update().catch(() => undefined)
  }
  const id = setI(check, opts.intervalMs ?? 60 * 60 * 1000)
  const onVisible = () => {
    if (doc?.visibilityState === 'visible' && now() - last >= minGap) check()
  }
  doc?.addEventListener('visibilitychange', onVisible)
  return () => {
    clearI(id)
    doc?.removeEventListener('visibilitychange', onVisible)
  }
}

export interface ApplyOptions {
  container?: SwContainer | null
  reload?: () => void
  /** If the new worker never takes control, reload anyway after this long. */
  timeoutMs?: number
}

/**
 * The user said Reload (or the gate decided to). Tell the waiting worker to activate, and reload
 * ONCE when it takes control. Only a controller change this call asked for reloads this tab: a
 * change caused by another tab does not (bindr's guard). No waiting worker: reload now.
 */
export function applyUpdate(reg: Pick<SwRegistration, 'waiting'>, opts: ApplyOptions = {}): void {
  const c = opts.container === undefined ? container() : opts.container
  const reload = opts.reload ?? (() => window.location.reload())
  const waiting = reg.waiting
  if (!c || !waiting) {
    reload()
    return
  }
  let done = false
  const once = () => {
    if (done) return
    done = true
    c.removeEventListener('controllerchange', once)
    reload()
  }
  c.addEventListener('controllerchange', once)
  setTimeout(once, opts.timeoutMs ?? 8000)
  waiting.postMessage(SKIP_WAITING)
}

export interface TakeOverOptions extends ApplyOptions {
  /** How long to wait for a new worker to finish installing. */
  installTimeoutMs?: number
}

export type TakeOverResult = 'reloading' | 'no-worker' | 'nothing-new' | 'error'

/**
 * The gate's `update` action (contently's `takeOverAndReload`, generalised): fetch the new worker,
 * wait for it to install, apply it. Resolves to what happened; 'reloading' means the page is about
 * to go. Anything else means the caller should fail open (render the app, offer the toast).
 * Never throws.
 */
export async function takeOverAndReload(opts: TakeOverOptions = {}): Promise<TakeOverResult> {
  const c = opts.container === undefined ? container() : opts.container
  const reload = opts.reload ?? (() => window.location.reload())
  if (!c) {
    // No worker support: the server already has the new index.html, so a plain reload takes it.
    reload()
    return 'reloading'
  }
  try {
    const reg = await c.getRegistration()
    if (!reg) {
      reload()
      return 'reloading'
    }
    await reg.update().catch(() => undefined)
    const waiting = reg.waiting ?? (await waitForInstalled(reg, opts.installTimeoutMs ?? 15000))
    if (!waiting) return 'nothing-new'
    applyUpdate({ waiting }, { ...opts, container: c, reload })
    return 'reloading'
  } catch {
    return 'error'
  }
}

function waitForInstalled(reg: SwRegistration, timeoutMs: number): Promise<SwWorker | null> {
  return new Promise((resolve) => {
    let settled = false
    const finish = (w: SwWorker | null) => {
      if (settled) return
      settled = true
      resolve(w)
    }
    const watch = (w: SwWorker | null) => {
      if (!w) return
      if (w.state === 'installed') finish(w)
      w.addEventListener('statechange', () => {
        if (w.state === 'installed') finish(w)
        if (w.state === 'redundant') finish(null)
      })
    }
    watch(reg.installing)
    reg.addEventListener('updatefound', () => watch(reg.installing))
    setTimeout(() => finish(reg.waiting), timeoutMs)
  })
}

// ─────────────────────────────────────────────────────────────────── a chunk from a gone build

/**
 * A tab open across a deploy asks for a lazy chunk the server no longer has. Cloudflare Pages in
 * SPA mode (W13: every app is pages-native) answers that missing `/assets/x.js` with index.html and
 * status 200, so the import fails with a MIME error: inkling's white screen, by the other door.
 * Vite fires `vite:preloadError` on window for exactly this. Reload onto the new build, and say so
 * to the caller when it gives up.
 *
 * ONLY for a chunk that genuinely failed to load (`isChunkLoadError`). Under Vite 8 the bundler
 * folds the `.then()` after a dynamic import into the preloaded callback, so `vite:preloadError`
 * also fires for ANY rejection in that chain: an unfiltered handler reloaded the page when OneDrive
 * refused a write (contently's error-log transport; its e2e auth failures went from 5 to 13). Any
 * other error is left alone, not even `preventDefault`ed, so Vite rethrows it to the app's own
 * handling as before.
 *
 * Loop guard: at most one automatic reload per `loopWindowMs` (default 10 minutes) per tab
 * session. A new build that is itself broken fails again at once and is left on screen (onGiveUp);
 * a later deploy, hours into the same session, still gets its reload.
 *
 * OFFLINE IS NOT STALE (decided 2026-10-10: five apps converged on it independently). With no
 * connection, a lazy chunk that was never cached fails with the very same error text as a chunk from
 * a gone build, and a reload cannot fix it: with no worker it lands on the browser's offline page,
 * with a worker it spends the one-reload guard and can eat a click. So while `isOnline()` is false
 * (default: `navigator.onLine === false`) the handler does nothing at all: no reload, the guard is
 * NOT spent (a genuine stale chunk after reconnecting still reloads), no `preventDefault`, so Vite
 * rethrows to the app's own error boundary. bindr (offlineChunkGuard.ts), inkling and contently
 * (onlineOnlyWindow in pwa.ts), quil (onlineOnlyWindow.ts) and trakr (chunkReloadGuard.ts) each wrote
 * this guard locally; they are folded in here. `reloadWhenOffline: true` restores the old
 * reload-regardless. Online behaviour is unchanged.
 * Returns a disposer.
 */
export function reloadOnStaleChunk(
  opts: {
    win?: Pick<Window, 'addEventListener' | 'removeEventListener'> | null
    storage?: KeyValue | null
    reload?: () => void
    onGiveUp?: (e: Event) => void
    loopWindowMs?: number
    now?: () => number
    /** Is the platform online? Default `navigator.onLine !== false` (unknown counts as online). */
    isOnline?: () => boolean
    /** Opt out of the offline rule: reload on a chunk failure even with no connection. */
    reloadWhenOffline?: boolean
  } = {},
): () => void {
  const win = opts.win === undefined ? (typeof window !== 'undefined' ? window : null) : opts.win
  if (!win) return () => undefined
  const storage = opts.storage === undefined ? safeSession() : opts.storage
  const reload = opts.reload ?? (() => window.location.reload())
  const now = opts.now ?? Date.now
  const windowMs = opts.loopWindowMs ?? 10 * 60 * 1000
  const isOnline = opts.isOnline ?? (() => typeof navigator === 'undefined' || navigator.onLine !== false)
  const KEY = 'app-update:chunk-reload'
  const mayReload = (): boolean => {
    try {
      if (!storage) return true
      const last = Number(storage.getItem(KEY))
      if (Number.isFinite(last) && last > 0 && now() - last < windowMs) return false
      storage.setItem(KEY, String(now() || 1))
      return true
    } catch {
      return true
    }
  }
  const handler = (e: Event) => {
    if (!isChunkLoadError(e)) return
    if (!opts.reloadWhenOffline && !isOnline()) return
    if (mayReload()) {
      e.preventDefault()
      reload()
    } else opts.onGiveUp?.(e)
  }
  win.addEventListener('vite:preloadError', handler)
  return () => win.removeEventListener('vite:preloadError', handler)
}

/**
 * The messages a chunk that could not be loaded carries: Chromium/Edge, Firefox, Safari, Vite's own
 * CSS preload, and the MIME refusal when Pages' SPA fallback answers a gone `/assets/x.js` with
 * index.html. A bare "Failed to fetch" is NOT one: that is any network call (a Graph write) that
 * rode the preload chain. Contently's local filter (src/lib/pwa.ts, W14) is where this came from.
 */
const CHUNK_LOAD_ERROR = /dynamically imported module|Importing a module script failed|Unable to preload CSS|Failed to load module script/i

/**
 * Is this `vite:preloadError` a chunk that could not be loaded (the stale-build case)?
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function isChunkLoadError(event: Event): boolean {
  const payload = (event as Event & { payload?: unknown }).payload
  const message = payload instanceof Error ? payload.message : typeof payload === 'string' ? payload : ''
  return CHUNK_LOAD_ERROR.test(message)
}

function safeSession(): KeyValue | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null
  } catch {
    return null
  }
}

// ─────────────────────────────────────────────────────────────────────── the toast's contract

export interface UpdateNotice {
  /** "A new version of Contently is ready." */
  message: string
  /** The what's-new line, or '' (render nothing). */
  note: string
  /** The incoming build's identity, e.g. "8 Oct 14:02 · a1b2c3d", or '' when unknown. */
  incoming: string
  /** The one action. Always "Reload": the same word in all five apps. */
  action: 'Reload'
  /** The dismiss control's accessible name. */
  dismissLabel: 'Not now'
}

/**
 * The words of the update toast. The toast is per app (its look is the app's design contract);
 * what it says and does is shared. Render as a polite live region (role="status"), never an alert:
 * an update is not an error. Dismissal hides it until the next new worker; it never skips the
 * update for good.
 */
export function updateNotice(appName: string, incoming?: BuildInfo | null, formatTime: (iso: string) => string = defaultTime): UpdateNotice {
  const note = (incoming?.note ?? '').trim()
  const bits: string[] = []
  const time = incoming?.time ? formatTime(incoming.time) : ''
  if (time) bits.push(time)
  if (incoming?.sha) bits.push(incoming.sha)
  return { message: `A new version of ${appName} is ready.`, note, incoming: bits.join(' · '), action: 'Reload', dismissLabel: 'Not now' }
}

function defaultTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
}
